import { useCallback, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { CellValueChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { LoaderCircleIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetBreedsQuery } from '@/app/services/lookups';
import { useDefaultGridOptions } from '@/grid';
import { CellRenderer } from '@/pages/bulk-donor-scheduling/components/cell-renderer';
import {
  type DonorMismatchData,
  DonorMismatchDialog,
} from '@/pages/bulk-donor-scheduling/components/donor-mismatch-dialog';
import { useDonorColumns } from '@/pages/bulk-donor-scheduling/hooks/use-columns';
import { useNotifications } from '@/pages/bulk-donor-scheduling/hooks/use-notifications';
import {
  type BulkDonorFieldsInput,
  BulkDonorFieldsSchema,
  type BulkDonorInput,
  BulkDonorRowsSchema,
} from '@/pages/bulk-donor-scheduling/utils/schema';
import {
  type BulkDonorValidationResponse,
  useUpsertBulkDonors,
  useValidateBulkDonors,
  type ValidateBulkDonorRequest,
  ValidationMessageLevel,
} from '@/services/bulk-donor';

import { type Messages, useBulkDonorStore } from './hooks/use-bulk-donor-store';

const normalizeBreedValue = (value: string) => value.trim().toLowerCase();

export function BulkDonorPage() {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();

  const [state, dispatch] = useBulkDonorStore();
  const { data: bovineBreeds } = useGetBreedsQuery({});

  const { handleSubmit, reset } = useForm<BulkDonorFieldsInput>({
    resolver: zodResolver(BulkDonorFieldsSchema),
  });

  const grid = useRef<AgGridReact<BulkDonorInput>>(null);
  const api = grid.current?.api;

  const [isGridEditing, setGridEditing] = useState(false);
  const [mismatchDialog, setMismatchDialog] = useState<{
    visible: boolean;
    rowIndex: number | null;
    donorName: string;
    mismatchData: DonorMismatchData | null;
  }>({
    visible: false,
    rowIndex: null,
    donorName: '',
    mismatchData: null,
  });

  const notifications = useNotifications();

  const validBovineBreedValues = useMemo(() => {
    const values = new Set<string>();
    for (const breed of bovineBreeds || []) {
      if (breed?.code) {
        values.add(normalizeBreedValue(breed.code));
      }
      if (breed?.name) {
        values.add(normalizeBreedValue(breed.name));
      }
    }
    return values;
  }, [bovineBreeds]);

  const columns = useDonorColumns({ readonly: state.readonly });

  const validateDonor = useValidateBulkDonors();
  const upsertDonorsMutation = useUpsertBulkDonors();

  const handleValidate = useCallback(
    async (_fields: BulkDonorFieldsInput) => {
      if (!api) {
        return;
      }

      dispatch({ type: 'client_validating' });
      api.stopEditing();

      const rows = state.rows.map((row) => {
        if (!row || Object.values(row).filter((v) => !!v).length === 0) {
          return undefined;
        }
        return row;
      });

      const rowsResult = BulkDonorRowsSchema.safeParse(rows);

      const inputs = rowsResult.data?.filter(
        (row): row is BulkDonorInput => row !== undefined,
      );

      if (!rowsResult.success || !inputs?.length) {
        const errors: Record<string, string> = {};

        const colId = api
          .getColumns()
          ?.find((c) => c.getColDef().field)
          ?.getColDef().field;
        if (!inputs?.length && colId) {
          errors[`${0}.${colId}`] = 'At least one valid row is required';
        }

        if (rowsResult.error?.issues) {
          for (const issue of rowsResult.error.issues) {
            const rowIndex = issue.path[0] as string;
            const colId = issue.path[1] as string;
            errors[`${rowIndex}.${colId}`] = issue.message;
          }
        }

        dispatch({ type: 'client_validated', errors });
        notifications.validationErrors();
        return;
      }

      if (bovineBreeds?.length) {
        const breedErrors: Record<string, string> = {};
        rowsResult.data.forEach((row, rowIndex) => {
          if (!row) {
            return;
          }

          const donorBreed = row.donorBreed?.trim();
          if (
            donorBreed &&
            !validBovineBreedValues.has(normalizeBreedValue(donorBreed))
          ) {
            breedErrors[`${rowIndex}.donorBreed`] =
              'Breed must be a valid bovine breed code or name';
          }
        });

        if (Object.keys(breedErrors).length > 0) {
          dispatch({ type: 'client_validated', errors: breedErrors });
          notifications.validationErrors();
          return;
        }
      }

      dispatch({ type: 'client_validated', errors: undefined });

      try {
        const variables: ValidateBulkDonorRequest = {
          inputs: rowsResult.data as BulkDonorInput[],
        };

        const result: BulkDonorValidationResponse =
          await validateDonor.mutateAsync(variables);

        let messages: Messages | undefined = undefined;
        let errors: Record<string, string> | undefined = undefined;

        for (const validation of result.fields) {
          const field = validation.field;
          if (!messages) {
            messages = {};
          }
          messages[field] = {
            level: ValidationMessageLevel.MESSAGE,
            message: validation.message,
          };
        }

        let hasMismatch = false;
        for (const validation of result.inputs) {
          const rowIndex = validation.index.toString();
          const colId = validation.field;

          if (
            validation.correctedValue &&
            validation.level === ValidationMessageLevel.MESSAGE
          ) {
            const updatedRow = {
              ...state.rows[validation.index],
              [colId]: validation.correctedValue,
            };
            dispatch({
              type: 'set_row',
              rowIndex: validation.index,
              data: updatedRow,
            });
          }

          if (validation.level === ValidationMessageLevel.ERROR) {
            if (!errors) {
              errors = {};
            }
            errors[`${rowIndex}.${colId}`] = validation.message;
          } else {
            if (!messages) {
              messages = {};
            }
            messages[`${rowIndex}.${colId}`] = {
              level: validation.level,
              message: validation.message,
              suggestions: validation.suggestions || undefined,
            };

            if (
              validation.level === ValidationMessageLevel.WARNING &&
              validation.mismatchData &&
              !hasMismatch
            ) {
              hasMismatch = true;
              const row = state.rows[validation.index];
              setMismatchDialog({
                visible: true,
                rowIndex: validation.index,
                donorName: row?.donorNameToMatch || '',
                mismatchData: validation.mismatchData,
              });
            }
          }
        }

        if (errors) {
          notifications.validationErrors();
        } else if (!hasMismatch) {
          notifications.validationSuccessful();
        }

        dispatch({ type: 'server_validated', errors, messages });
      } catch (error) {
        notifications.validationServerError();
        dispatch({ type: 'server_error' });
        console.error('Error while validating data', error);
      }
    },
    [
      api,
      dispatch,
      notifications,
      state.rows,
      validateDonor,
      bovineBreeds,
      validBovineBreedValues,
    ],
  );

  const handleInvalidSubmit = useCallback(() => {
    notifications.validationErrors();
  }, [notifications]);

  const handleUpload = useCallback(async () => {
    if (!api) {
      return;
    }

    dispatch({ type: 'uploading' });

    try {
      const rows = BulkDonorRowsSchema.parse(
        state.rows.filter((row) => {
          return !!row && !!Object.values(row).filter((v) => !!v).length;
        }),
      ).filter((row): row is BulkDonorInput => row !== undefined);

      await upsertDonorsMutation.mutateAsync(rows);

      notifications.uploadSuccessful();
      dispatch({ type: 'success' });
    } catch (error) {
      notifications.uploadServerError();
      dispatch({ type: 'server_error' });
      console.error('Error while uploading data', error);
    }
  }, [api, dispatch, notifications, state.rows, upsertDonorsMutation]);

  const handleCellValueChanged = useCallback(
    (e: CellValueChangedEvent<BulkDonorInput>) => {
      const rowIndex = e.rowIndex;
      const data = e.data;
      if (rowIndex === null || data === undefined) {
        return;
      }

      const focusedCell = e.api.getFocusedCell();

      dispatch({ type: 'set_row', rowIndex, data });

      if (focusedCell) {
        e.api.stopEditing();

        setTimeout(() => {
          e.api.setFocusedCell(
            focusedCell.rowIndex,
            focusedCell.column,
            focusedCell.rowPinned,
          );
          e.api.startEditingCell({
            rowIndex: focusedCell.rowIndex,
            colKey: focusedCell.column,
            rowPinned: focusedCell.rowPinned,
          });
        }, 300);
      }
    },
    [dispatch],
  );

  const handleUseExistingData = useCallback(() => {
    if (mismatchDialog.rowIndex === null || !mismatchDialog.mismatchData) {
      return;
    }

    const rowIndex = mismatchDialog.rowIndex;
    const currentRow = state.rows[rowIndex];
    if (!currentRow) {
      return;
    }

    const updatedRow: BulkDonorInput = {
      ...currentRow,
      donorRegistrationNumber:
        mismatchDialog.mismatchData.existingRegistrationNumber ||
        currentRow.donorRegistrationNumber,
      pedigreeName:
        mismatchDialog.mismatchData.existingPedigreeName ||
        currentRow.pedigreeName,
      birthDate:
        mismatchDialog.mismatchData.existingBirthDate || currentRow.birthDate,
    };

    dispatch({ type: 'set_row', rowIndex, data: updatedRow });

    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });

    notifications.validationSuccessful();
  }, [mismatchDialog, state.rows, dispatch, notifications]);

  const handleCreateNewDonor = useCallback(() => {
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });

    notifications.validationSuccessful();
  }, [notifications]);

  const handleCancelMismatch = useCallback(() => {
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });
  }, []);

  return (
    <div className="h-[calc(100%-4rem)]">
      <DonorMismatchDialog
        visible={mismatchDialog.visible}
        mismatchData={mismatchDialog.mismatchData}
        donorName={mismatchDialog.donorName}
        onUseExisting={handleUseExistingData}
        onCreateNew={handleCreateNewDonor}
        onCancel={handleCancelMismatch}
      />

      <form
        onSubmit={handleSubmit(handleValidate, handleInvalidSubmit)}
        className="mb-4 flex flex-col justify-between gap-4 lg:flex-row"
      >
        <div className="flex flex-col gap-2 md:flex-row" />
        <div className="flex flex-wrap gap-2">
          {state.type === 'server_validated' ||
          state.type === 'uploading' ||
          state.type === 'success' ? (
            <Button
              severity="success"
              className="flex-grow justify-center"
              disabled={
                state.loading || state.type === 'success' || isGridEditing
              }
              onClick={handleUpload}
            >
              {state.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Uploading')}
                </>
              ) : (
                t('bulkDonorScheduling.Upload')
              )}
            </Button>
          ) : (
            <Button
              className="justify-center"
              disabled={state.loading || isGridEditing}
              type="submit"
            >
              {state.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Validating')}
                </>
              ) : (
                t('bulkDonorScheduling.Validate')
              )}
            </Button>
          )}

          <Button
            className="justify-center"
            disabled={
              state.loading || state.type === 'success' || isGridEditing
            }
            onClick={() => {
              dispatch({ type: 'add_rows', count: 10 });
            }}
          >
            {t('bulkDonorScheduling.Add_Rows')}
          </Button>

          <Button
            className="justify-center"
            size="small"
            severity="danger"
            disabled={state.loading || isGridEditing}
            onClick={() => {
              reset();
              dispatch({ type: 'reset' });
            }}
          >
            {t('bulkDonorScheduling.Reset')}
          </Button>
        </div>
      </form>

      <div className="h-full w-full overflow-auto">
        <AgGridReact
          gridOptions={defaultGridOptions}
          ref={grid}
          className="ag-theme-transova h-full [--ag-border-radius:0]"
          suppressCsvExport
          suppressExcelExport
          suppressHorizontalScroll={false}
          tooltipInteraction
          tooltipShowDelay={0}
          context={state}
          defaultColDef={{
            cellRenderer: CellRenderer,
            resizable: true,
            suppressHeaderMenuButton: true,
            tooltipValueGetter(params) {
              const rowIndex = params.rowIndex;
              const fieldName =
                params.colDef &&
                'field' in params.colDef &&
                (params.colDef.field as string);
              if (rowIndex === undefined || !fieldName) {
                return;
              }

              const error = state.errors?.[`${rowIndex}.${fieldName}`];
              const message =
                'messages' in state &&
                state.messages?.[`${rowIndex}.${fieldName}`]?.message;

              return error || message;
            },
            cellStyle: (params) => {
              const baseStyle: Record<string, string> = {
                borderRight: '1px solid var(--ag-border-color)',
              };

              if (state.type === 'success') {
                return { ...baseStyle, borderRightColor: 'var(--color-emerald-500)' };
              }

              const rowIndex = params.rowIndex;
              const fieldName =
                params.colDef &&
                'field' in params.colDef &&
                params.colDef.field;
              if (!fieldName) {
                return baseStyle;
              }

              const hasMessage =
                'messages' in state &&
                state.messages?.[`${rowIndex}.${fieldName}`]?.level ===
                  ValidationMessageLevel.MESSAGE;
              const hasWarning =
                'messages' in state &&
                state.messages?.[`${rowIndex}.${fieldName}`]?.level ===
                  ValidationMessageLevel.WARNING;
              const hasError = !!state.errors?.[`${rowIndex}.${fieldName}`];

              if (hasError) {
                return { ...baseStyle, borderColor: 'var(--color-red-500)', padding: '0' };
              }
              if (hasWarning) {
                return { ...baseStyle, borderColor: 'var(--color-amber-500)', padding: '0' };
              }
              if (hasMessage) {
                return { ...baseStyle, borderColor: 'var(--color-emerald-500)', padding: '0' };
              }
              return baseStyle;
            },
          }}
          columnDefs={columns}
          onCellValueChanged={handleCellValueChanged}
          rowData={state.rows}
          rowStyle={
            state.type === 'success'
              ? {
                  borderColor: 'var(--color-emerald-500)',
                  backgroundColor: 'color-mix(in srgb, var(--ag-background-color), var(--ag-foreground-color) 10%)',
                }
              : state.readonly
                ? { backgroundColor: 'color-mix(in srgb, var(--ag-background-color), var(--ag-foreground-color) 10%)' }
                : undefined
          }
          enableRangeSelection
          enableFillHandle
          autoSizePadding={2}
          stopEditingWhenCellsLoseFocus
          onCellEditingStarted={() => setGridEditing(true)}
          onCellEditingStopped={() => setGridEditing(false)}
        />
      </div>
    </div>
  );
}
